/**
 * Site Visit Tracker — agent-facing GPS arrival/departure block.
 *
 * Behaviour:
 *   1. Asks the browser for location permission on mount.
 *   2. Watches `navigator.geolocation` continuously while the page is open.
 *   3. Pings the backend every 30 seconds with the current lat/lng.
 *   4. Backend tells us if the agent is inside a registered property
 *      (geofence) and whether a visit has been auto-started.
 *   5. When a visit is active, shows a live timer and an End Visit button.
 *   6. When auto-arrival is detected for the first time, fires a popup so
 *      the agent can confirm or dismiss the auto-start.
 *
 * Browser limitation: GPS only updates while this tab is open. True
 * background tracking would need a mobile app — that's documented in the
 * help tooltip on the card.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, Navigation, Clock, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';
import HelpInfo from './HelpInfo';

const API = process.env.REACT_APP_BACKEND_URL;
const PING_INTERVAL_MS = 30 * 1000;
// Working-day window — the close-confirmation prompt is active during
// these hours so an agent is nudged to keep the tracker open. (24h
// clock, local time.)
const OFFICE_START_HOUR = 9;
const OFFICE_END_HOUR = 18;

const formatDuration = (seconds) => {
  if (seconds == null || seconds < 0) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  if (m) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

const SiteVisitTracker = () => {
  const { token } = useAuth();
  const [permission, setPermission] = useState('prompt'); // prompt | granted | denied | unsupported
  const [position, setPosition] = useState(null);
  const [activeVisit, setActiveVisit] = useState(null);
  const [nearest, setNearest] = useState(null);
  const [arrivalPrompt, setArrivalPrompt] = useState(null); // shown once per geofence entry
  const [tickNow, setTickNow] = useState(Date.now());
  // True while the agent is on approved leave today — the tracker
  // pauses entirely so it doesn't log visits on a non-working day.
  const [onLeave, setOnLeave] = useState(false);
  const watchIdRef = useRef(null);
  const pingTimerRef = useRef(null);
  const lastPromptedFenceRef = useRef(null);

  // 1-second tick keeps the visit timer fresh.
  useEffect(() => {
    const t = setInterval(() => setTickNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // On leave today? Ask the backend once on mount. If yes, every other
  // effect below bails out — no GPS watch, no pings, no close warning.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/site-visits/my-tracking-state?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (!cancelled && data?.success) setOnLeave(!!data.on_leave);
      } catch (e) {
        // non-fatal — default to tracking enabled
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Keep the tab open during the working day. Browsers don't let a
  // page actually block a close — the most they allow is a confirm
  // dialog — and a web page genuinely can't keep running once the
  // browser is fully shut (that needs a native app). So we make the
  // agent confirm: the warning fires through office hours OR whenever
  // a visit is in progress, and is suppressed when the agent is on
  // leave. If they do leave, the visit stays open server-side and can
  // be closed later from "I'm Back".
  useEffect(() => {
    if (onLeave) return undefined;
    const onBeforeUnload = (e) => {
      const hour = new Date().getHours();
      const inOfficeHours = hour >= OFFICE_START_HOUR && hour < OFFICE_END_HOUR;
      if (!activeVisit && !inOfficeHours) return undefined;
      e.preventDefault();
      // Browsers show a generic message, but returnValue must be set
      // for the prompt to appear at all.
      e.returnValue = activeVisit
        ? 'A site visit is still in progress. Close anyway? The visit stays open — reopen this tab to keep tracking.'
        : 'Site Visit Tracker is running for the working day. Closing it stops GPS auto-detection. Close anyway?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [activeVisit, onLeave]);

  const ping = useCallback(async (lat, lng) => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/site-visits/ping?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat, lng }),
      });
      const data = await res.json();
      if (!data.success) return;
      setNearest(data.nearest || null);
      setActiveVisit(data.active_visit || null);
      // Surface the arrival popup once per geofence entry.
      if (data.auto_started && data.nearest && lastPromptedFenceRef.current !== data.nearest.id) {
        lastPromptedFenceRef.current = data.nearest.id;
        setArrivalPrompt(data.nearest);
        toast.success(`Arrived at ${data.nearest.name}. Site visit auto-started.`);
      }
    } catch (e) {
      console.warn('[GPS] ping failed', e);
    }
  }, [token]);

  // Set up geolocation watch + 30s polling. We use both: watchPosition fires
  // on movement (low-energy) and the 30s ping ensures the backend hears from
  // us even if the agent stays still.
  useEffect(() => {
    if (!token) return;
    // On leave today → don't track at all.
    if (onLeave) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setPermission('unsupported');
      return;
    }

    const onPos = (p) => {
      setPosition({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy });
      setPermission('granted');
    };
    const onErr = (err) => {
      if (err.code === 1) setPermission('denied'); // PERMISSION_DENIED
      console.warn('[GPS] error', err);
    };

    watchIdRef.current = navigator.geolocation.watchPosition(onPos, onErr, {
      enableHighAccuracy: true,
      maximumAge: 15_000,
      timeout: 30_000,
    });

    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, [token, onLeave]);

  // Whenever we have a fresh position, ping the backend immediately, then
  // every 30s as long as we have one.
  useEffect(() => {
    if (!position || !token || onLeave) return;
    ping(position.lat, position.lng);
    pingTimerRef.current = setInterval(() => {
      if (position) ping(position.lat, position.lng);
    }, PING_INTERVAL_MS);
    return () => clearInterval(pingTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position?.lat, position?.lng, token, onLeave]);

  const startManualVisit = useCallback(async () => {
    if (!position) return;
    try {
      const res = await fetch(`${API}/api/site-visits/start?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat: position.lat, lng: position.lng, auto_detected: false }),
      });
      const data = await res.json();
      if (data.success) {
        setActiveVisit(data.visit);
        setArrivalPrompt(null);
        toast.success('Site visit started');
      } else {
        toast.error(data.detail || 'Could not start visit — are you near a property?');
      }
    } catch (e) {
      toast.error(`Start failed: ${e.message || e}`);
    }
  }, [token, position]);

  const endVisit = useCallback(async () => {
    if (!activeVisit) return;
    try {
      const res = await fetch(
        `${API}/api/site-visits/${activeVisit.id}/end?token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lat: position?.lat, lng: position?.lng }),
        },
      );
      const data = await res.json();
      if (data.success) {
        toast.success(`Visit ended · ${formatDuration(data.visit?.duration_seconds || 0)}`);
        setActiveVisit(null);
        lastPromptedFenceRef.current = null;
      }
    } catch (e) {
      toast.error(`End failed: ${e.message || e}`);
    }
  }, [token, activeVisit, position]);

  const arrivalSeconds = activeVisit?.arrival_time
    ? Math.max(0, Math.floor((tickNow - new Date(activeVisit.arrival_time).getTime()) / 1000))
    : 0;

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Navigation className="w-4 h-4 text-emerald-600" />
          Site Visit Tracker
          <HelpInfo
            align="inline"
            title="How the Site Visit Tracker works"
            steps={[
              'Your browser shares its GPS location with the app — the small pin at the right shows your current coordinates.',
              <>The app continuously checks whether you're standing inside any registered <em>property geofence</em> (a virtual radius drawn around each property).</>,
              'When you arrive at a property, a visit auto-starts and a timer begins counting your time on site.',
              'When you leave the geofence radius, the visit auto-closes and the post-visit report dialog pops up so you can record the outcome.',
              <>If GPS auto-detect doesn't catch a visit (poor signal, brief stop), you can also log it manually from <em>Log Field Visit</em> above and it'll show up as a <strong>Manual</strong> entry.</>,
              <>During working hours the browser asks you to confirm before closing this tab — keep it open so auto-detection keeps running. A visit already started stays open server-side either way and can be closed later from <em>I'm Back</em>.</>,
              <>On a day you're on <strong>approved leave</strong>, the tracker pauses itself automatically — no GPS, no pings, no visits logged — and resumes on your next working day.</>,
              <>If your browser blocks location, click the location icon in the address bar and allow it — the tracker won't fire visits without permission.</>,
            ]}
            tip="A web page can't track once the browser is fully closed — keep this tab open while you're in the field, or log visits manually if you can't."
          />
          <span className="ml-auto text-xs font-normal text-slate-500">
            {permission === 'granted' && position
              ? `📍 ${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}`
              : permission === 'denied'
                ? '🚫 Location blocked'
                : permission === 'unsupported'
                  ? 'GPS not supported in this browser'
                  : 'Waiting for location…'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* On approved leave — tracking is paused for the day. */}
        {onLeave && (
          <div className="flex items-start gap-2 p-3 rounded-lg border border-blue-200 bg-blue-50 text-sm">
            <AlertCircle size={16} className="text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">You're on approved leave today</p>
              <p className="text-blue-700 text-xs mt-0.5">
                Site visit tracking is paused — no GPS is recorded and no visits are
                logged on a leave day. It resumes automatically on your next working day.
              </p>
            </div>
          </div>
        )}
        {permission === 'denied' && !onLeave && (
          <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm">
            <AlertCircle size={16} className="text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">Location access blocked</p>
              <p className="text-amber-700 text-xs mt-0.5">
                Allow location for this site in your browser settings to enable auto-arrival
                detection. Until then you can still log visits manually after enabling.
              </p>
            </div>
          </div>
        )}

        {/* Active visit panel — live timer and End Visit */}
        {activeVisit ? (
          <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="font-medium text-emerald-900">
                    On site at {activeVisit.property_name || 'property'}
                  </span>
                  {activeVisit.auto_detected && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 border text-xs">
                      Auto-detected
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-emerald-800 mt-1 font-mono">
                  <Clock size={12} className="inline mr-1" />
                  Visit duration: {formatDuration(arrivalSeconds)}
                </div>
              </div>
              <Button onClick={endVisit} variant="outline" className="border-emerald-500 text-emerald-700">
                End Visit
              </Button>
            </div>
          </div>
        ) : nearest ? (
          // Inside a geofence but no active visit → manual start fallback.
          <div className="p-4 rounded-lg border border-blue-200 bg-blue-50">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-blue-600" />
                  <span className="font-medium text-blue-900">Within range of {nearest.name}</span>
                </div>
                <p className="text-xs text-blue-800 mt-1">
                  {nearest.distance_metres}m from property centre · radius {nearest.radius_metres}m
                </p>
              </div>
              <Button onClick={startManualVisit} className="bg-blue-600 hover:bg-blue-500">
                Start Site Visit
              </Button>
            </div>
          </div>
        ) : !onLeave ? (
          <p className="text-sm text-slate-500">
            {permission === 'granted'
              ? 'Not currently within any property’s geofence. The app will auto-start a visit the moment you arrive.'
              : 'Once location access is granted, arrivals at registered properties will be detected automatically.'}
          </p>
        ) : null}
      </CardContent>

      {/* Arrival popup — shown once per geofence entry. The ping has already
          auto-started the visit by this point, so the popup is a confirmation. */}
      {arrivalPrompt && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(15, 23, 42, 0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div className="w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <MapPin className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold mb-1">You have arrived</h2>
            <p className="text-sm text-slate-500 mb-4">{arrivalPrompt.name}</p>
            <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 mb-4">
              Site visit auto-started · timer is running
            </p>
            <div className="flex gap-3 justify-center">
              <button
                type="button"
                onClick={() => setArrivalPrompt(null)}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
              >
                <X size={14} /> Dismiss
              </button>
              <button
                type="button"
                onClick={() => setArrivalPrompt(null)}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

    </Card>
  );
};

export const SiteVisitReportDialog = ({ visit, token, onClose, onSubmitted }) => {
  const [outcome, setOutcome] = useState('');
  const [productive, setProductive] = useState(null);
  const [notes, setNotes] = useState('');
  const [nextSteps, setNextSteps] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!outcome && !notes && productive === null && !nextSteps) {
      toast('Pick an outcome or add a note before submitting');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(
        `${API}/api/site-visits/${visit.id}/report?token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ outcome, productive, notes, next_steps: nextSteps }),
        },
      );
      const data = await res.json();
      if (data.success) {
        toast.success('Visit report submitted');
        // Tell the parent the report landed so it can refresh the
        // "My Site Visit Reports" list straight away.
        onSubmitted?.(data.visit || visit);
        onClose();
      } else {
        toast.error(data.detail || 'Could not save report');
      }
    } catch (e) {
      toast.error(`Save failed: ${e.message || e}`);
    } finally {
      setSubmitting(false);
    }
  };

  const outcomes = [
    'Met client', 'Client no-show', 'Showing completed',
    'Inspection done', 'Met broker', 'Other',
  ];
  const durationLabel = visit.duration_seconds != null
    ? `${Math.floor(visit.duration_seconds / 60)}m ${visit.duration_seconds % 60}s`
    : '';

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
        backgroundColor: 'rgba(15, 23, 42, 0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div className="w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Visit report</h2>
            <p className="text-xs text-slate-500">
              {visit.property_name}{durationLabel ? ` · ${durationLabel} on site` : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Outcome</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {outcomes.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setOutcome(o)}
                  className={`px-2.5 py-1 rounded-full text-xs border ${
                    outcome === o
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Was this productive?</label>
            <div className="flex gap-2 mt-1">
              {[
                { v: true, label: 'Yes' },
                { v: false, label: 'No' },
              ].map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setProductive(opt.v)}
                  className={`px-3 py-1 rounded-md text-xs border ${
                    productive === opt.v
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">What happened?</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Brief summary — who you met, key discussion points…"
              className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Next steps (optional)</label>
            <textarea
              value={nextSteps}
              onChange={(e) => setNextSteps(e.target.value)}
              rows={2}
              placeholder="Send proposal, schedule follow-up, share floor plans…"
              className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : 'Submit report'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SiteVisitTracker;
